/**
 * 返事の下の小さな記録の行「カレンダーを引きました · 15:58」（ADR-0055）。DOM を持たない部分。
 *
 * 何をしたかは、返事と一緒に届いた道具の記録（ResponderResponse.tools_used と同じ形。Gateway が final に添えるようになったら使う）
 * から機械的に作る。届かない間は、承認待ちができたなら「承認待ち」、道具の往復があった（流した文の取り消しがあった）なら
 * 「調べて答えました」、どれも無ければ時刻だけ。文は言い換えない・増やさない（事実は全て入力から）。
 */

export interface ToolUseView {
  tool: string;
  status: string;
}

export interface RecordInput {
  /** 返事と一緒に届いた道具の記録（無ければ undefined） */
  tools_used?: unknown;
  /** この返事でできた承認待ち */
  approval_ids?: string[];
  /** 流した文の取り消しの数（道具の呼び出しが始まった印。ADR-0018） */
  retracts?: number;
  /** 返事が届いた時刻。無ければ時刻は出さない（履歴の返事） */
  at?: Date | null;
  /** 返事の途中で止めた（ADR-0042） */
  interrupted?: boolean;
}

/** 道具の名前（tools.<name>）→ したことの言い方。無い名前は「道具を使いました」 */
const TOOL_LABEL: Record<string, string> = {
  calendar: "カレンダーを引きました",
  "calendar.read": "カレンダーを引きました",
  "calendar.write": "カレンダーに書きました",
  calendar_add: "カレンダーに書きました",
  drive: "ドライブを読みました",
  "drive.read": "ドライブを読みました",
  lists: "リストを更新しました",
  "lists.manage": "リストを更新しました",
  calc: "計算しました",
  "calc.evaluate": "計算しました",
  search: "検索しました",
  web_search: "検索しました",
  "web_search.query": "検索しました",
  remember: "覚えました",
  "self.status": "自分の様子を見ました",
  self_status: "自分の様子を見ました",
  jobs_start: "作業係に頼みました",
  "jobs.start": "作業係に頼みました",
  jobs_status: "作業係の様子を見ました",
  "jobs.status": "作業係の様子を見ました",
  jobs_amend: "作業係に追加の指示を出しました",
  "jobs.amend": "作業係に追加の指示を出しました",
  jobs_cancel: "作業係の仕事を取り消しました",
  "jobs.cancel": "作業係の仕事を取り消しました",
};

/** 道具の記録の形だけ残す（他は捨てる） */
export function toolUsesOf(v: unknown): ToolUseView[] {
  if (!Array.isArray(v)) return [];
  return v.filter(
    (t): t is ToolUseView =>
      !!t &&
      typeof t === "object" &&
      typeof (t as ToolUseView).tool === "string" &&
      typeof (t as ToolUseView).status === "string",
  );
}

export function timeLabel(d: Date): string {
  return d.toLocaleTimeString("ja-JP", { hour: "2-digit", minute: "2-digit" });
}

/** したことの部分（時刻を除く）。無ければ null */
export function didLabel(input: RecordInput): string | null {
  const used = toolUsesOf(input.tools_used).filter((t) => t.status === "executed");
  if (used.length > 0) {
    const labels = [...new Set(used.map((t) => TOOL_LABEL[t.tool] ?? "道具を使いました"))];
    return labels.join("・");
  }
  if (input.approval_ids?.length) return "承認待ち";
  if (input.interrupted) return "ここで止めました";
  if ((input.retracts ?? 0) > 0) return "調べて答えました";
  return null;
}

/** 行の全文「カレンダーを引きました · 15:58」。何も無ければ null（行を出さない） */
export function recordLine(input: RecordInput): string | null {
  const parts = [didLabel(input), input.at ? timeLabel(input.at) : null].filter(
    (s): s is string => !!s,
  );
  return parts.length ? parts.join(" · ") : null;
}
